import {
	REGEX_LEADING_DASH,
	REGEX_MULTIPLE_DASH,
	REGEX_NON_WORD_CHARS,
	REGEX_TRAILING_DASH,
	REGEX_WHITE_SPACE
} from '../../constants/regular-expression';
import { SITE_BASE_URL } from '../../constants/site';

/**
 * Converts value into a slug for urls
 */
export const convertToSlug = (value: string): string =>
	value
		.replace(REGEX_WHITE_SPACE, '-')
		.replace(REGEX_NON_WORD_CHARS, '-')
		.replace(REGEX_MULTIPLE_DASH, '-')
		.replace(REGEX_LEADING_DASH, '')
		.replace(REGEX_TRAILING_DASH, '')
		.toLowerCase();

/**
 * Takes a full path and strips out the query string if it exists
 */
export function extractPathName(fullPath: string): string {
	const url = new URL(fullPath, SITE_BASE_URL);
	return url.pathname;
}

/**
 * Determines if a URL is external.
 */
export const isExternalUrl = (url: string): boolean => {
	try {
		const parsed = new URL(url, SITE_BASE_URL);
		return parsed.origin !== SITE_BASE_URL;
	} catch {
		return false;
	}
};

/**
 * Add a new search param to a url
 *
 * @param {string} path relative url
 * @param {string} key new param key
 * @param {string} value new param value
 * @returns {string}
 */
export function setUrlParam(path: string, key: string, value: string): string {
	const parsedURL = new URL(path, 'https://www.fergusonhome.com');
	parsedURL.searchParams.set(key, value);
	return `${parsedURL.pathname}${parsedURL.search}`;
}

/**
 * Gets case insensitive query param
 */
export const getQueryParamValue = (searchKey: string, search: string): string | undefined => {
	const params = new URLSearchParams(search);
	const lowerCaseSearchKey = searchKey.toLowerCase();

	for (const [key, value] of params.entries()) {
		if (key.toLowerCase() === lowerCaseSearchKey) {
			return value;
		}
	}

	return undefined;
};

/**
 * Gets case insensitive query params for multiple keys
 */
export const getQueryParamValues = (searchKeys: string[], search: string): Record<string, string | undefined> => {
	return searchKeys.reduce<Record<string, string | undefined>>((acc, key) => {
		acc[key] = getQueryParamValue(key, search);
		return acc;
	}, {});
};

/**
 * Removes query param from URL search param
 * For example: removeQueryParamFromSearch('query1', '?query1=test1&query2=test2')
 * would return '?query2=test2'
 */
export const removeSearchParam = (searchKey: string, search: string): string => {
	const searchParams = new URLSearchParams(search);
	searchParams.delete(searchKey);
	return searchParams.toString();
};

/**
 * Generates a product URL based on manufacturer, product ID, family ID, and optional unique ID.
 * TODO: EFDC-34616 - make required params not optional. The `uniqueId` should remain optional.
 */
export function generateProductUrl(
	manufacturerName: string | null | undefined,
	productId: string | null | undefined,
	familyId: number | null | undefined,
	uniqueId?: number | null
): string {
	const uidQueryString = uniqueId || uniqueId === 0 ? `?uid=${uniqueId}` : '';
	const slug = convertToSlug(`${manufacturerName}-${productId}`);
	return `/${slug}/s${familyId}${uidQueryString}`;
}
